import { useRef, useState } from 'react'
import type { Message } from '../../api/types'
import { pointBelow, useSheetTriggers } from '../../hooks/gestures/useSheetTriggers'
import { useGestureTip } from '../../state/gestureTip'
import { useConversation } from './ConversationContext'
import type { MessageSheetRequest } from './MessageSheet'
import type { MessageRules } from './messageRules'
import { usePrivateReply } from './privateReply'
import { useSwipeToReply } from './useSwipeToReply'

/** Selected text in the message: right-click then keeps the browser's menu, to copy part of it. */
function hasSelectionIn(element: Element): boolean {
  const selection = window.getSelection()
  return Boolean(selection && !selection.isCollapsed && selection.anchorNode && element.contains(selection.anchorNode))
}

interface Options {
  message: Message
  rules: MessageRules
  /** Messages still sending, deleted or being edited have no sheet and don't move. */
  actionable: boolean
  /** As the sheet opens, e.g. to close the quick reaction picker. */
  onOpen: () => void
}

/**
 * What a message does besides its own buttons (spec §3): swipe to reply, and the sheet from a hold (or the phone's
 * long-press menu), right-click, the Menu key and the Actions and More buttons. Spread `rowProps` on the message.
 */
export function useMessageSheetTriggers({ message, rules, actionable, onOpen }: Options) {
  const { channel, startReply } = useConversation()
  const replyPrivately = usePrivateReply()
  const rowRef = useRef<HTMLElement>(null)
  const [request, setRequest] = useState<MessageSheetRequest | null>(null)
  // Deleted by someone else while its sheet was open: the sheet and the highlight go.
  if (request && !actionable) setRequest(null)

  const open = (sheet: Omit<MessageSheetRequest, 'view'>, view: MessageSheetRequest['view'] = 'actions') => {
    onOpen()
    // However the sheet was asked for, a touch screen lifts the bubble itself (the whole row for a photo or a file).
    const row = rowRef.current
    setRequest({ ...sheet, anchor: row?.querySelector<HTMLElement>('.bubble') ?? row, view })
  }

  const swipe = useSwipeToReply(
    rowRef,
    actionable && rules.swipe
      ? () => {
          useGestureTip.getState().markUsed()
          if (rules.swipe === 'reply') startReply(message)
          else void replyPrivately(channel.id, message.id)
        }
      : null,
  )

  const triggers = useSheetTriggers({
    focusRef: rowRef,
    enabled: actionable,
    open: request !== null,
    swipe,
    // A right-click on a link, a photo or selected text keeps the browser's own menu.
    keepBrowserMenu: (event) => Boolean((event.target as Element).closest('a[href], img')) || hasSelectionIn(event.currentTarget),
    // Android's own long-press menu, which comes first, opens the sheet this way too.
    onHold: () => useGestureTip.getState().markUsed(),
    onOpen: (sheet) => open(sheet),
  })

  return {
    rowRef,
    rowProps: triggers.rowProps,
    /** The reply arrow is only there where the message can be swiped. */
    swipeable: swipe !== null,
    request,
    close: () => setRequest(null),
    /** The Actions and More buttons: focus comes back to the button. */
    openFrom: triggers.openFrom,
    /** + in the quick reactions, which close as the picker opens: focus comes back to the message. */
    openEmojiFrom: (button: HTMLElement) => open({ at: pointBelow(button), returnFocus: rowRef.current, touch: false }, 'emoji'),
  }
}
